import { useToast } from '../../../../shared/components/SharedUI';
import { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useCatalogo } from "../hooks/useCatalogo";
import { CuentaNode } from "../components/CuentaNode";
import { CuentaModal } from "../components/CuentaModal";
import styles from "../../invoices/styles/invoice.module.css";

const TIPO_INFO = {
  ACTIVO:     { label: "Activos",        icon: "fa-building-columns", color: "#60a5fa" },
  PASIVO:     { label: "Pasivos",        icon: "fa-arrow-trend-down", color: "#f87171" },
  PATRIMONIO: { label: "Patrimonio",     icon: "fa-shield-halved",    color: "#a78bfa" },
  INGRESO:    { label: "Ingresos",       icon: "fa-arrow-trend-up",   color: "#4ade80" },
  GASTO:      { label: "Gastos/Costos",  icon: "fa-receipt",          color: "#fbbf24" },
};

function contarCuentas(nodo) {
  let total = 1;
  for (const hijo of nodo.subcuentas ?? []) total += contarCuentas(hijo);
  return total;
}

export default function ListCatalogo() {
  const { showToast, toastEl } = useToast();
  const { arbol, loading, error, crear, editar, activar, refetch, previewEliminar, eliminar } = useCatalogo();

  // ── Estado de acordeones — vive AQUÍ, sobrevive al refetch ────────────────
  // Inicialmente todas las raíces expandidas (IDs se agregan cuando el árbol carga)
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [initializedRoots, setInitializedRoots] = useState(false);

  // Cuando el árbol carga por primera vez, expandir las raíces
  if (arbol.length > 0 && !initializedRoots) {
    setExpandedIds(new Set(arbol.map((r) => r.id)));
    setInitializedRoots(true);
  }

  const handleToggle = useCallback((id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Modales
  const [modal,      setModal]      = useState(null);
  const [confirmDes,  setConfirmDes]  = useState(null);   // {id, nombre} — pre-eliminar
  const [elimWarning, setElimWarning] = useState(null);   // {advertencia, cuentas_a_eliminar}
  const [elimLoading, setElimLoading] = useState(false);
  const [search,     setSearch]     = useState("");
  const [tipoFiltro, setTipoFiltro] = useState("");

  // Filtrar árbol visible
  const arbolFiltrado = arbol.filter((r) => !tipoFiltro || r.tipo === tipoFiltro);

  async function handleCrear(data) {
    await crear(data);
    // No tocar expandedIds — los acordeones quedan como estaban
  }

  async function handleEditar(data) {
    await editar(modal.cuenta.id, data);
  }

  async function handleEliminarClick() {
    // Step 1: get warning from server
    setElimLoading(true);
    try {
      const data = await previewEliminar(confirmDes.id);
      setElimWarning(data);
    } catch (e) {
      showToast("Error: " + e.message, false);
      setConfirmDes(null);
    } finally {
      setElimLoading(false);
    }
  }

  async function handleEliminarConfirm() {
    setElimLoading(true);
    try {
      await eliminar(confirmDes.id);
      setConfirmDes(null);
      setElimWarning(null);
    } catch (e) {
      showToast("Error al eliminar: " + e.message, false);
    } finally {
      setElimLoading(false);
    }
  }

  return (
    <>
      {toastEl}

      <span className={styles.inactive}>
        <Link to="/">
          <span className={styles.inactive}>Inicio /</span>
        </Link>
          <span className={styles.active}> Catálogo de Cuentas</span>
      </span>

      {/* Header */}
      <div style={{
        display: "flex", justifyContent: "space-between",
        alignItems: "flex-start", flexWrap: "wrap", gap: 12, margin: "8px 0 20px",
      }}>
        <div>
          <h2 className="title" style={{ margin: 0, color: "#f9fafb"}}>
            Catálogo de Cuentas
          </h2>
          <p style={{ margin: "4px 0 0", color: "#9ca3af", fontSize: "1rem" }}>
            Solo cuentas de detalle son usables en transacciones
          </p>
        </div>
        <button onClick={refetch}
          style={{
            padding: "8px 14px", background: "rgba(55,65,81,0.4)",
            border: "none", borderRadius: 6, color: "#9ca3af",
            cursor: "pointer", fontSize: "0.82rem",
            display: "flex", alignItems: "center", gap: 6,
            transition: "color 0.2s",
          }}
          onMouseOver={(e) => (e.currentTarget.style.color = "#f9fafb")}
          onMouseOut={(e)  => (e.currentTarget.style.color = "#9ca3af")}
        >
          <i className="fa-solid fa-arrows-rotate" /> Actualizar
        </button>
      </div>

      {/* Tarjetas resumen */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
        gap: 8, marginBottom: 20,
      }}>
        {arbol.map((raiz) => {
          const info  = TIPO_INFO[raiz.tipo] || TIPO_INFO.ACTIVO;
          const total = contarCuentas(raiz) - 1;
          const activo = tipoFiltro === raiz.tipo;
          return (
            <button
              key={raiz.id}
              type="button"
              onClick={() => setTipoFiltro(activo ? "" : raiz.tipo)}
              style={{
                background: activo ? `${info.color}18` : "#1f2937",
                border: `1px solid ${activo ? info.color + "55" : "#374151"}`,
                borderRadius: 8, padding: "12px 14px",
                textAlign: "left", cursor: "pointer",
                transition: "all 0.15s",
              }}
            >
              <i className={`fa-solid ${info.icon}`}
                style={{ color: info.color, marginBottom: 6, display: "block" }} />
              <div style={{ color: "#6b7280", fontSize: "0.67rem",
                fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                {info.label}
              </div>
              <div style={{ color: "#f9fafb", fontSize: "0.8rem",
                fontWeight: 700, marginTop: 2, fontFamily: "monospace" }}>
                {raiz.codigo}
              </div>
              <div style={{ color: "#6b7280", fontSize: "0.7rem", marginTop: 2 }}>
                {total} cuenta{total !== 1 ? "s" : ""}
              </div>
            </button>
          );
        })}
      </div>

      {/* Tabla árbol */}
      <div style={{
        background: "#1f2937", border: "1px solid #374151",
        borderRadius: 8, overflow: "hidden",
      }}>
        {/* Barra de búsqueda */}
        <div style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "10px 14px", borderBottom: "1px solid #374151",
          background: "rgba(55,65,81,0.2)",
        }}>
          <i className="fa-solid fa-magnifying-glass" style={{ color: "#6b7280", fontSize: "0.78rem" }} />
          <input
            type="text"
            placeholder="Buscar por código o nombre…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              flex: 1, background: "none", border: "none",
              color: "#f9fafb", fontSize: "0.85rem", outline: "none",
            }}
          />
          {tipoFiltro && (
            <button type="button" onClick={() => setTipoFiltro("")}
              style={{ background: "none", border: "none",
                color: "#f97316", cursor: "pointer", fontSize: "0.78rem" }}>
              <i className="fa-solid fa-xmark" style={{ marginRight: 4 }} />
              {TIPO_INFO[tipoFiltro]?.label}
            </button>
          )}
          <span style={{ fontSize: "0.68rem", color: "#374151" }}>
            <i className="fa-solid fa-circle" style={{ color: "#f97316", fontSize: "0.45rem", marginRight: 4 }} />
            DETALLE = usable en facturas
          </span>
        </div>

        {/* Cabecera columnas */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "38px 8px 112px 1fr auto 80px",
          gap: 0, padding: "6px 14px",
          borderBottom: "1px solid #374151",
          background: "rgba(55,65,81,0.2)",
        }}>
          {["", "", "Código", "Nombre", "Tipo", "Acciones"].map((h, i) => (
            <span key={i} style={{
              fontSize: "0.65rem", color: "#6b7280",
              fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px",
            }}>{h}</span>
          ))}
        </div>

        {loading && (
          <div style={{ textAlign: "center", padding: 40, color: "#6b7280" }}>
            <i className="fa-solid fa-circle-notch fa-spin"
              style={{ color: "#f97316", fontSize: "1.2rem", marginBottom: 8, display: "block" }} />
            Cargando catálogo…
          </div>
        )}

        {error && (
          <div style={{ padding: 24, color: "#f87171", textAlign: "center" }}>
            <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: 8 }} />
            {error}
          </div>
        )}

        {!loading && !error && (
          <>
            {arbolFiltrado
              .filter((r) =>
                !search ||
                r.codigo.includes(search) ||
                r.nombre.toLowerCase().includes(search.toLowerCase())
              )
              .map((raiz) => (
                <CuentaNode
                  key={raiz.id}
                  cuenta={raiz}
                  depth={0}
                  expandedIds={expandedIds}
                  onToggle={handleToggle}
                  onAgregar={(c) => setModal({ type: "create", padre: c })}
                  onEditar={(c)  => setModal({ type: "edit",   cuenta: c })}
                  onDesactivar={(id, nombre) => { setConfirmDes({ id, nombre }); }}
                  onActivar={(id) => activar(id)}
                />
              ))
            }
            {arbolFiltrado.length === 0 && (
              <div style={{ padding: 40, textAlign: "center", color: "#6b7280" }}>
                No hay cuentas que mostrar.
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal crear/editar */}
      {modal?.type === "create" && (
        <CuentaModal
          mode="create" padre={modal.padre}
          onClose={() => setModal(null)} onSave={handleCrear}
        />
      )}
      {modal?.type === "edit" && (
        <CuentaModal
          mode="edit" cuenta={modal.cuenta}
          onClose={() => setModal(null)} onSave={handleEditar}
        />
      )}

      {/* Eliminar cuenta contable — paso 1: confirmar intención */}
      {confirmDes && !elimWarning && (
        <div style={{position:"fixed",inset:0,zIndex:1000,background:"rgba(0,0,0,0.65)",
          backdropFilter:"blur(4px)",display:"flex",alignItems:"center",justifyContent:"center"}}
          onClick={() => { setConfirmDes(null); setElimWarning(null); }}>
          <div style={{background:"#1f2937",border:"1px solid #374151",borderRadius:10,
            padding:28,width:"min(400px,92vw)"}} onClick={e => e.stopPropagation()}>
            <div style={{textAlign:"center",marginBottom:18}}>
              <div style={{fontSize:"2.2rem",marginBottom:8}}>🗑️</div>
              <h3 style={{color:"#f1f5f9",margin:0}}>¿Eliminar cuenta?</h3>
              <p style={{color:"#9ca3af",fontSize:"0.85rem",marginTop:8}}>
                Se eliminará <strong style={{color:"#f97316"}}>{confirmDes.nombre}</strong>{" "}
                y todas sus subcuentas permanentemente, junto con los asientos que las referencien.
              </p>
            </div>
            <div style={{display:"flex",gap:8,justifyContent:"center"}}>
              <button onClick={() => { setConfirmDes(null); }}
                style={{padding:"8px 20px",background:"rgba(55,65,81,0.4)",
                  border:"1px solid #374151",borderRadius:7,color:"#9ca3af",cursor:"pointer"}}>
                Cancelar
              </button>
              <button onClick={handleEliminarClick} disabled={elimLoading}
                style={{padding:"8px 20px",background:"rgba(239,68,68,0.15)",
                  border:"1px solid rgba(239,68,68,0.4)",borderRadius:7,color:"#f87171",
                  cursor:"pointer",fontWeight:700,opacity:elimLoading?0.6:1}}>
                {elimLoading ? "Verificando…" : "Continuar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Eliminar cuenta contable — paso 2: advertencia detallada */}
      {confirmDes && elimWarning && (
        <div style={{position:"fixed",inset:0,zIndex:1000,background:"rgba(0,0,0,0.75)",
          backdropFilter:"blur(4px)",display:"flex",alignItems:"center",justifyContent:"center"}}
          onClick={() => { setConfirmDes(null); setElimWarning(null); }}>
          <div style={{background:"#1f2937",border:"1px solid #374151",borderRadius:10,
            padding:24,width:"min(480px,94vw)",maxHeight:"80vh",overflow:"auto"}}
            onClick={e => e.stopPropagation()}>
            <div style={{textAlign:"center",marginBottom:16}}>
              <div style={{fontSize:"2.2rem",marginBottom:8}}>⚠️</div>
              <h3 style={{color:"#f1f5f9",margin:0}}>Confirmar eliminación</h3>
              <p style={{color:"#f87171",fontSize:"0.85rem",marginTop:8,lineHeight:1.6}}>
                {elimWarning.advertencia}
              </p>
            </div>
            {elimWarning.cuentas_a_eliminar?.length > 0 && (
              <div style={{background:"rgba(55,65,81,0.3)",borderRadius:6,padding:"10px 14px",
                marginBottom:16,fontSize:"0.78rem"}}>
                <p style={{color:"#9ca3af",margin:"0 0 6px",fontWeight:700,
                  textTransform:"uppercase",fontSize:"0.7rem"}}>
                  Cuentas a eliminar:
                </p>
                {elimWarning.cuentas_a_eliminar.map((c,i) => (
                  <div key={i} style={{color:"#e5ecf5",fontFamily:"monospace",marginBottom:2}}>
                    {c.codigo} — {c.nombre}
                  </div>
                ))}
              </div>
            )}
            <div style={{display:"flex",gap:8,justifyContent:"center"}}>
              <button onClick={() => { setConfirmDes(null); setElimWarning(null); }}
                style={{padding:"9px 20px",background:"rgba(55,65,81,0.4)",
                  border:"1px solid #374151",borderRadius:7,color:"#9ca3af",cursor:"pointer"}}>
                Cancelar
              </button>
              <button onClick={handleEliminarConfirm} disabled={elimLoading}
                style={{padding:"9px 20px",background:"rgba(239,68,68,0.15)",
                  border:"1px solid rgba(239,68,68,0.4)",borderRadius:7,color:"#f87171",
                  cursor:"pointer",fontWeight:700,opacity:elimLoading?0.6:1}}>
                {elimLoading ? "Eliminando…" : "Sí, eliminar permanentemente"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
